import { useCallback, useContext, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import useFCMService from "../../../../global/helpers/FCMService";
import useDispositivoVinculacion from "../../../hooks/useDispositivoVinculacion";
import { PortalContext } from "../../../context/Provider";
import { GlobalContext } from "../../../../global/context/Provider";
import { wsGetNotificacion } from "../../../context/Actions/notificaciones/notificaciones";
import { setNewtokenFCM } from "../../../context/Actions/token/tokenFCM";
import isAuthenticated from "../../../../global/utils/isAuthenticated";
import {
  setPendingPushUrl,
  consumePendingPushUrl,
  setPendingPushAction,
  consumePendingPushAction,
} from "../../../../global/helpers/pendingPushUrl";
import { runPushAction, resolverActionId } from "../../../../global/helpers/pushActions";

// Key en Preferences donde el PushActionReceiver.java (Android) persiste el
// {actionId, data, savedAt} cuando el user tapea un boton de la notif con la
// app killed/background. Debe matchear exactamente el string del receiver.
const PENDING_ACTION_PREFERENCE_KEY = "pending-push-action";

/**
 * Limpia el badge del icono + notifs entregadas.
 * removeDeliveredNotifications({notifications:[]}) sin ids no borra todo — hay que
 * pedir la lista primero. En iOS esto tambien resetea el badge como side-effect.
 */
const limpiarBadgeYNotifs = async () => {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { notifications } = await FirebaseMessaging.getDeliveredNotifications();
    if (notifications?.length) {
      await FirebaseMessaging.removeDeliveredNotifications({ notifications });
    }
  } catch (err) {
    console.warn("limpiarBadgeYNotifs fallo:", err);
  }
};

/**
 * Valida que la URL del push sea una ruta interna del portal ("/misInformes").
 * Rechaza absolutas ("http://…"), protocol-relative ("//evil.tld/x") y no-strings.
 * Sin esto, un push malicioso o mal armado podria navegar fuera del portal.
 */
const esRutaInternaValida = (url) => {
  return typeof url === "string" && url.startsWith("/") && !url.startsWith("//");
};

/**
 * Lee y consume la accion pendiente que el PushActionReceiver.java grabo en
 * SharedPreferences cuando el user tapeo un boton de la notif Android.
 *
 * En iOS este flujo no aplica — la accion llega por el evento nativo
 * notificationActionPerformed del plugin (ver FCMService.js).
 */
const consumirPendingPushActionDePreferences = async () => {
  if (!Capacitor.isNativePlatform()) return null;
  try {
    const { value } = await Preferences.get({ key: PENDING_ACTION_PREFERENCE_KEY });
    if (!value) return null;
    await Preferences.remove({ key: PENDING_ACTION_PREFERENCE_KEY });
    const parsed = JSON.parse(value);
    if (!parsed?.actionId) return null;
    return { actionId: parsed.actionId, data: parsed.data || {} };
  } catch (err) {
    console.warn("consumirPendingPushActionDePreferences fallo:", err);
    return null;
  }
};

/**
 * PushBootstrap — registra listeners de FCM al montar el Router.
 *
 * Motivo del "temprano": en iOS con la app killed, el evento
 * pushNotificationActionPerformed se dispara dentro de los primeros ~2s
 * del arranque. Si registramos los listeners post-login, perdemos el tap
 * inicial y el user aterriza en la home en vez de la página destino.
 *
 * initPush() (que solicita permisos + register) se llama en un useEffect
 * separado que depende de authState — solo se pide permiso cuando el user
 * ya está autenticado, nunca antes.
 *
 * En web (Capacitor.isNativePlatform() === false) es no-op.
 */
const PushBootstrap = () => {
  const history = useHistory();
  const { authState } = useContext(GlobalContext);
  const { notificacionesDispatch, tokenFCMDispatch } = useContext(PortalContext);

  // Guard para el useEffect post-login: evita repetir limpiarBadge + consumePendingUrl
  // en cada re-emision de authState.auth.data (que puede ocurrir por re-hidrataciones
  // del reducer sin cambio real). Se resetea en logout.
  const postLoginRunRef = useRef(false);

  const onPushReceived = useCallback(() => {
    wsGetNotificacion()(notificacionesDispatch);
  }, [notificacionesDispatch]);

  // Tap sobre el push: si actionId es "tap" (banner completo) navega a data.url.
  // Si es un actionId de boton (como-llegar, ver-preparacion) delega a runPushAction.
  // Excepcion: el tap sobre un push de confirmacion se trata como el boton
  // "confirmar-turno" (ver pushActions.resolverActionId) — en iOS los botones solo
  // aparecen con long-press y el tap simple tiene que llegar al modal igual.
  //
  // Si la app estaba killed el user aun no esta autenticado (viene por
  // biometria/login), asi que dejamos la url/accion pendiente y la consumimos
  // en el useEffect de abajo cuando el auth termine — sino el guard de las
  // rutas la redirige a "/" y se pierde.
  const onPushAction = useCallback(
    ({ data, actionId: actionIdOriginal }) => {
      limpiarBadgeYNotifs();

      const actionId = resolverActionId(actionIdOriginal, data);
      const esBoton = actionId && actionId !== "tap";

      if (esBoton) {
        if (isAuthenticated()) {
          runPushAction({ actionId, data, history });
        } else {
          setPendingPushAction({ actionId, data });
        }
        return;
      }

      // Tap sobre el banner completo — comportamiento anterior.
      if (!esRutaInternaValida(data?.url)) return;
      if (isAuthenticated()) {
        history.push(data.url);
      } else {
        setPendingPushUrl(data.url);
      }
    },
    [history]
  );

  const onTokenUpdate = useCallback(
    (token) => {
      setNewtokenFCM(token)(tokenFCMDispatch);
    },
    [tokenFCMDispatch]
  );

  const { initListeners, initPush } = useFCMService({
    onTokenUpdate,
    onPushReceived,
    onPushAction,
  });

  useEffect(() => {
    if (Capacitor.isNativePlatform()) initListeners();
  }, [initListeners]);

  useEffect(() => {
    if (Capacitor.isNativePlatform() && authState?.auth?.data) {
      initPush();
    }
  }, [authState?.auth?.data, initPush]);

  // Consume acciones/urls pendientes cuando el auth se completa (caso app killed).
  // Prioridad: preferences nativas (Android) > acciones in-memory > urls in-memory.
  // Ademas limpia el badge del icono.
  const consumirPendientesPostAuth = useCallback(async () => {
    limpiarBadgeYNotifs();

    // (1) Preferences Android: lo llena el PushActionReceiver.java cuando el user
    //     tapea un boton con la app killed/background.
    const fromPrefs = await consumirPendingPushActionDePreferences();
    if (fromPrefs) {
      runPushAction({ ...fromPrefs, history });
      // Descartamos in-memory por si tambien quedaron seteadas (mismo evento).
      consumePendingPushAction();
      consumePendingPushUrl();
      return;
    }

    // (2) In-memory action: iOS con notificationActionPerformed pre-auth.
    const pendingAction = consumePendingPushAction();
    if (pendingAction) {
      runPushAction({ ...pendingAction, history });
      consumePendingPushUrl();
      return;
    }

    // (3) In-memory url: tap del banner pre-auth (iOS o Android sin categoria).
    const pending = consumePendingPushUrl();
    if (esRutaInternaValida(pending)) history.push(pending);
  }, [history]);

  useEffect(() => {
    if (!authState?.auth?.data) {
      postLoginRunRef.current = false;
      return;
    }
    if (postLoginRunRef.current) return;
    postLoginRunRef.current = true;
    consumirPendientesPostAuth();
  }, [authState?.auth?.data, consumirPendientesPostAuth]);

  // Cuando la app vuelve del background al foreground (por ejemplo, el user tapeo
  // un boton de la notif con la app en background, y Android relanzo MainActivity),
  // rechequeamos las Preferences. Sin esto, el flujo (1) solo corre en el primer
  // login del session lifecycle y perdemos las acciones que llegan despues.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    if (!authState?.auth?.data) return;

    const sub = App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) consumirPendientesPostAuth();
    });
    return () => {
      // Capacitor 6 devuelve una Promise<PluginListenerHandle>; el remove es async.
      sub.then?.((h) => h.remove()).catch(() => {});
    };
  }, [authState?.auth?.data, consumirPendientesPostAuth]);

  // Hook que registra/actualiza el dispositivo del paciente en el back cuando
  // hay sesión iniciada (solo mobile). Consume el fcmToken del reducer tokenFCM
  // que llena el listener 'tokenReceived' de FCM (ver onTokenUpdate arriba).
  useDispositivoVinculacion();

  return null;
};

export default PushBootstrap;
